/**
 * Modo Forja Sound Effects
 * Alarm-style sounds for countdown warnings and session transitions
 */

import { getAudioContext } from './audioManager';

// Start flow - gentle chime (kept for session start)
const START_SOUND = 'data:audio/wav;base64,UklGRl4FAABXQVZFZm10IBAAAAABAAEAIlYAAHCoAAABAAgAZGF0YToFAAB/f39/f39/f39/gICBgoSFh4mLjY+RkpSVlpeXl5eWlZSTkY+NioiGhIOCgYB/f39/f39/f4CAgYKEhYeJi42PkZKUlZaXl5eXlpWUk5GPjYqIhoSDgoGAf39/f39/f3+AgIGChIWHiYuNj5GSlJWWl5eXl5aVlJORj42KiIaEg4KBgH9/f39/f39/gICBgoSFh4mLjY+RkpSVlpeXl5eWlZSTkY+NioiGhIOCgYB/f39/f39/f4CAgYKEhYeJi42PkZKUlZaXl5eXlpWUk5GPjYqIhoSDgoGAf39/f39/f3+AgIGChIWHiYuNj5GSlJWWl5eXl5aVlJORj42KiIaEg4KBgH9/f39/f39/gICBgoSFh4mLjY+RkpSVlpeXl5eWlZSTkY+NioiGhIOCgYB/fn19fHx8fX5/gIKEhomLjpCSlJWWl5eXlpWUkpCOi4mGhIKAf359fHx8fX5/gYOFiIqNj5GUlZaXl5eWlZOSkI6LiYaEgoB/fn18fHx9fn+Bg4WIio2PkZSVlpeXl5aVk5KQjouJhoSCgH9+fXx8fH1+f4GDhYiKjY+RlJWWl5eXlpWTkpCOi4mGhIKAf359fHx8fX5/gYOFiIqNj5GUlZaXl5eWlZOSkI6LiYaEgoB/fn18fHx9fn+Bg4WIio2PkZSVlpeXl5aVk5KQjouJhoSCgH9+fXx8fH1+f4GDhYiKjY+RlJWWl5eXlpWTkpCOi4mGhIKAf359fHx8fX5/gYOFiIqNj5GUlZaXl5eWlZOSkI6LiYaEgoB/fn18fHx9fn+Bg4WIio2PkZSVlpeXl5aVk5KQjouJhoSCgH9+fXx8fH1+f4GDhYiKjY+RlJWWl5eXlpWTkpCOi4mGhIKAf359fHx8fX5/gYOFiIqNj5GUlZaXl5eWlZOSkI6LiYaEgoB/fn18fHx9fn+Bg4WIio2PkZSVlpeXl5aVk5KQjouJhoSCgH9+fXx8fH1+f4GDhYiKjY+RlJWWl5eXlpWTkpCOi4mGhIKAf359fHx8fX5/gYOFiIqNj5GUlZaXl5eWlZOSkI6LiYaEgoB/fn18fHx9fn+Bg4WIio2PkZSVlpeXl5aVk5KQjouJhoSCgH9/f39/f39/f4CAgYKEhYeJi42PkZKUlZaXl5eXlpWUk5GPjYqIhoSDgoGAf39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/';

// Shared audio context reference
let audioContext: AudioContext | null = null;

const getContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = getAudioContext();
  }
  return audioContext;
};

/**
 * Play a base64 encoded sound
 */
const playSound = async (base64Data: string, volume: number = 0.4): Promise<void> => {
  try {
    const ctx = getContext();
    
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    
    // Decode base64
    const base64 = base64Data.split(',')[1];
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Decode and play
    const audioBuffer = await ctx.decodeAudioData(bytes.buffer);
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    
    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;
    
    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start();
  } catch (e) {
    // Fallback to Audio element
    try {
      const audio = new Audio(base64Data);
      audio.volume = volume;
      await audio.play();
    } catch {
      // Silent fail - audio is not critical
    }
  }
};

/**
 * Play countdown beep with escalating urgency (5→1 seconds)
 * Frequency and volume increase as time runs out
 */
export const playCountdownBeep = async (secondsLeft: number): Promise<void> => {
  try {
    const ctx = getContext();
    if (ctx.state === 'suspended') await ctx.resume();
    
    const now = ctx.currentTime;
    
    // Frequency increases as time decreases (more urgent)
    const baseFreq = 880; // A5
    const freqMultiplier = 1 + ((5 - secondsLeft) * 0.15); // 1.0 → 1.6
    const freq = baseFreq * freqMultiplier;
    
    // Volume increases in final seconds
    const volume = 0.2 + ((5 - secondsLeft) * 0.1); // 0.2 → 0.6
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'square'; // Alarm-like tone
    osc.frequency.value = freq;
    
    // Envelope: fast attack, short decay
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.02);
    gain.gain.linearRampToValueAtTime(0, now + 0.15);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.2);
  } catch {
    // Silent fail
  }
};

/**
 * Play alarm sound when session ends (classic alarm pattern)
 */
export const playAlarmSound = async (): Promise<void> => {
  try {
    const ctx = getContext();
    if (ctx.state === 'suspended') await ctx.resume();
    
    const now = ctx.currentTime;
    
    // 5 pulses alternating between two frequencies
    const pulses = [
      { time: 0, freq: 880 },      // A5
      { time: 0.25, freq: 1100 },  // C#6
      { time: 0.5, freq: 880 },    // A5
      { time: 0.75, freq: 1100 },  // C#6
      { time: 1.0, freq: 880 },    // A5
    ];
    
    pulses.forEach(({ time, freq }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'square';
      osc.frequency.value = freq;
      
      const start = now + time;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.5, start + 0.03);
      gain.gain.linearRampToValueAtTime(0, start + 0.18);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  } catch {
    // Silent fail
  }
};

/**
 * Play sound when entering forja (starting focus)
 */
export const playStartForjaSound = async (): Promise<void> => {
  await playSound(START_SOUND, 0.3);
};

/**
 * Play transition sound when any session ends (focus or break)
 */
export const playSessionEndSound = async (): Promise<void> => {
  await playAlarmSound();
};
